import { Action, createReducer, on } from "@ngrx/store";

import { DataRightsAdminActions } from "../../actions/data-rights-admin.actions";
import { DataRightsAdminState } from "../../models/mp-admin/data-rights-admin-state";

const initialState: DataRightsAdminState = {
	items: [],
	consumerGroups: {},
	loading: false,
	rightsItems: [],
	rightsLoading: false
};

const reducer = createReducer(
	initialState,

	// Group: search
	on(DataRightsAdminActions.group.search, (state) => ({ ...state, loading: true })),
	on(DataRightsAdminActions.group.searchSuccess, (state, { result }) => ({
		...state,
		items: result.items ?? [],
		nextKeyset: result.nextKeyset,
		totalCount: result.totalCount,
		hasLoaded: true,
		loading: false,
		error: undefined
	})),
	on(DataRightsAdminActions.group.searchFail, (state, { error }) => ({
		...state,
		loading: false,
		error
	})),

	// Group: getById
	on(DataRightsAdminActions.group.getById, (state) => ({ ...state, loading: true })),
	on(DataRightsAdminActions.group.getByIdSuccess, (state, { group }) => ({
		...state,
		currentGroup: group,
		loading: false,
		error: undefined
	})),
	on(DataRightsAdminActions.group.getByIdFail, (state, { error }) => ({
		...state,
		loading: false,
		error
	})),

	// Group: create / update
	on(DataRightsAdminActions.group.createSuccess, (state, { group }) => ({
		...state,
		currentGroup: group,
		items: [...state.items, toSummary(group)],
		loading: false,
		error: undefined
	})),
	on(DataRightsAdminActions.group.updateSuccess, (state, { group }) => ({
		...state,
		currentGroup: group,
		items: state.items.map((i) => (i.id === group.id ? toSummary(group) : i)),
		loading: false,
		error: undefined
	})),
	on(
		DataRightsAdminActions.group.createFail,
		DataRightsAdminActions.group.updateFail,
		(state, { error }) => ({ ...state, loading: false, error })
	),

	// Group: delete
	on(DataRightsAdminActions.group.deleteSuccess, (state, { id }) => ({
		...state,
		items: state.items.filter((i) => i.id !== id),
		currentGroup: state.currentGroup?.id === id ? undefined : state.currentGroup,
		loading: false,
		error: undefined
	})),
	on(DataRightsAdminActions.group.deleteFail, (state, { error }) => ({
		...state,
		loading: false,
		error
	})),

	// Group: promote default — BE promotes one, demotes the old default.
	on(DataRightsAdminActions.group.promoteDefaultSuccess, (state, { group }) => ({
		...state,
		currentGroup: state.currentGroup?.id === group.id ? group : state.currentGroup,
		items: state.items.map((i) => ({
			...i,
			isDefault: i.id === group.id
		})),
		loading: false,
		error: undefined
	})),
	on(DataRightsAdminActions.group.promoteDefaultFail, (state, { error }) => ({
		...state,
		loading: false,
		error
	})),

	// Group: membership
	on(DataRightsAdminActions.group.setMembershipSuccess, (state, { group }) => ({
		...state,
		currentGroup: group,
		loading: false,
		error: undefined
	})),
	on(DataRightsAdminActions.group.setMembershipFail, (state, { error }) => ({
		...state,
		loading: false,
		error
	})),

	// Available rights (consumer-scoped dropdown)
	on(DataRightsAdminActions.availableRights.get, (state) => ({ ...state, loading: true })),
	on(DataRightsAdminActions.availableRights.getSuccess, (state, { rights }) => ({
		...state,
		availableRights: rights,
		loading: false,
		error: undefined
	})),
	on(DataRightsAdminActions.availableRights.getFail, (state, { error }) => ({
		...state,
		loading: false,
		error
	})),

	// Available groups (consumer-scoped, with files + rights)
	on(DataRightsAdminActions.availableGroups.get, (state) => ({ ...state, loading: true })),
	on(DataRightsAdminActions.availableGroups.getSuccess, (state, { groups }) => ({
		...state,
		availableGroups: groups,
		loading: false,
		error: undefined
	})),
	on(DataRightsAdminActions.availableGroups.getFail, (state, { error }) => ({
		...state,
		loading: false,
		error
	})),

	// Consumer assignments
	on(
		DataRightsAdminActions.consumer.getSuccess,
		DataRightsAdminActions.consumer.setSuccess,
		(state, { consumerId, groups }) => ({
			...state,
			consumerGroups: { ...state.consumerGroups, [consumerId]: groups },
			loading: false,
			error: undefined
		})
	),
	on(
		DataRightsAdminActions.consumer.getFail,
		DataRightsAdminActions.consumer.setFail,
		(state, { error }) => ({ ...state, loading: false, error })
	),

	// Rights CRUD
	on(DataRightsAdminActions.rights.list, (state) => ({ ...state, rightsLoading: true })),
	on(DataRightsAdminActions.rights.listSuccess, (state, { result }) => ({
		...state,
		rightsItems: result.items ?? [],
		rightsTotalCount: result.totalCount,
		rightsLoading: false,
		error: undefined
	})),
	on(DataRightsAdminActions.rights.listFail, (state, { error }) => ({
		...state,
		rightsLoading: false,
		error
	})),
	on(DataRightsAdminActions.rights.createSuccess, (state, { right }) => ({
		...state,
		rightsItems: [...state.rightsItems, right],
		rightsLoading: false,
		error: undefined
	})),
	on(DataRightsAdminActions.rights.updateSuccess, (state, { right }) => ({
		...state,
		rightsItems: state.rightsItems.map((r) => (r.id === right.id ? right : r)),
		rightsLoading: false,
		error: undefined
	})),
	on(DataRightsAdminActions.rights.deleteSuccess, (state, { id }) => ({
		...state,
		rightsItems: state.rightsItems.filter((r) => r.id !== id),
		rightsLoading: false,
		error: undefined
	})),
	on(
		DataRightsAdminActions.rights.createFail,
		DataRightsAdminActions.rights.updateFail,
		DataRightsAdminActions.rights.deleteFail,
		(state, { error }) => ({ ...state, rightsLoading: false, error })
	)
);

function toSummary(group: DataRightsAdminState["currentGroup"]) {
	if (!group) {
		return {};
	}
	return {
		id: group.id,
		name: group.name,
		description: group.description,
		isDefault: group.isDefault,
		consumerIds: group.consumerIds,
		consumers: group.consumers,
		createdAt: group.createdAt,
		createdBy: group.createdBy,
		updatedAt: group.updatedAt,
		updatedBy: group.updatedBy,
		version: group.version
	};
}

export function dataRightsAdminReducer(
	state: DataRightsAdminState | undefined,
	action: Action
): DataRightsAdminState {
	return reducer(state, action);
}
